/**
 * Persistent outbox for document media uploads — the disk/IndexedDB layer the
 * upload orchestrator (documentAttachmentService.js) feeds from.
 *
 * One record per staged upload: a meta JSON object plus the ENCRYPTED
 * ciphertext blob, keyed by the node's pendingRef. Meta shape:
 *
 *   { id, docId, scopeKey, scope, name, mime, size, w, h, keyB64,
 *     state: 'queued'|'uploading'|'error'|'uploaded-unwritten',
 *     attempts, lastError, url, createdAtMs }
 *
 * `scope` is the { type, teamId } descriptor the upload POST needs;
 * `scopeKey` is the extension_documents bucket it resolved to at enqueue time
 * (`personal:local`, `personal:<userId>`, `team:<teamId>`) — the tag
 * `retagScope` rewrites when sign-in folds signed-out drafts into the user's
 * scope, mirroring `personal_migrate_local_scope`.
 *
 * Two adapters behind one interface, chosen by isTauri():
 *
 *  - TAURI: plain files under `$APPLOCALDATA/doc-media-outbox/` — `<id>.bin`
 *    (ciphertext, writeFile) + `<id>.json` (meta, writeTextFile).
 *    DELIBERATELY OUTSIDE the per-account root: services/accountData.js
 *    documents that everything under `users/<accountId>/` is purged wholesale
 *    on sign-out BECAUSE it is re-derivable (mirrors, scratch bodies). Queued
 *    upload ciphertext is NOT re-derivable — the plaintext lives nowhere else
 *    once the paste buffer is gone — so it lives outside that root for the
 *    same reason `local-workspaces/` does. Ownership is expressed by
 *    `scopeKey` in the meta instead of by directory, exactly like the
 *    signed-out drafts the retag models itself on. The tree is granted in
 *    src-tauri/capabilities/default.json (guarded by capabilityFsScope.test.js).
 *
 *  - WEB: IndexedDB db 'apiboo-doc-media-outbox' v1, store 'items' keyed by
 *    the meta id, records { meta, blob }. Raw IDB, no dependencies. HONESTY
 *    NOTE: jsdom has no IndexedDB, so the web adapter's real-IDB behavior is
 *    UNTESTED — unit tests exercise the interface contract through the
 *    in-memory fake below and the Tauri adapter against a mocked plugin-fs;
 *    the IDB code path has only been reasoned about, not executed under test.
 *    Where IndexedDB is missing entirely the adapter degrades instead of
 *    throwing at call sites that can tolerate it: list() -> [],
 *    readCiphertext() -> null, enqueue rejects (the service then falls back
 *    to chunk 8's in-memory-only behavior).
 *
 * Lifecycle: items stay until uploaded AND rewritten into the stored document
 * (or explicitly removed). There is NO garbage-collection sweep in this v1 —
 * a crash between `enqueue` and the editor inserting the pending node leaves
 * an orphan item that uploads, finds no node to rewrite, and is removed then
 * (a doc deleted THIS SESSION is dropped earlier still, via deletedDocsGuard
 * in the service — no POST at all; only a doc deleted in a PAST session can
 * reach the upload, because the guard's marks do not survive restarts);
 * an orphan whose document never resolves (e.g. deleted while the item was in
 * 'error') persists until its upload succeeds and the rewrite finds nothing.
 * Documented trade-off: bounded by failed pastes, never silently lossy.
 */
import { isTauri } from '../utils/tauri';

export const OUTBOX_DIR = 'doc-media-outbox';
export const OUTBOX_IDB_NAME = 'apiboo-doc-media-outbox';
export const OUTBOX_IDB_VERSION = 1;
export const OUTBOX_IDB_STORE = 'items';

// pendingRefs are minted by documentAttachmentService.mintPendingRef and match
// the node schema's DOC_MEDIA_PENDING_REF_RE. Validated here AGAIN because ids
// become file names: a meta file tampered on disk must never turn a later
// updateMeta/remove into a path escaping the outbox directory.
const OUTBOX_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

function validId(id) {
  return typeof id === 'string' && OUTBOX_ID_RE.test(id);
}

/**
 * Blob -> Uint8Array. `Blob.arrayBuffer()` where the runtime has it (every
 * real webview); FileReader fallback for jsdom 16, whose Blob lacks it.
 * Shared with DocImageComponent's post-restart decrypt path.
 */
export function readBlobBytes(blob) {
  if (blob && typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buf) => new Uint8Array(buf));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error || new Error('blob read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/* ── Tauri adapter — $APPLOCALDATA/doc-media-outbox/ ─────────────────────── */

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

export function createTauriOutboxAdapter() {
  let dirReady = null;

  async function ensureDir(fs) {
    if (!dirReady) {
      dirReady = fs
        .mkdir(OUTBOX_DIR, { baseDir: fs.BaseDirectory.AppLocalData, recursive: true })
        .catch((err) => {
          dirReady = null; // retry on the next write
          throw err;
        });
    }
    return dirReady;
  }

  const binPath = (id) => `${OUTBOX_DIR}/${id}.bin`;
  const metaPath = (id) => `${OUTBOX_DIR}/${id}.json`;

  return {
    async enqueue(meta, ciphertextBlob) {
      if (!meta || !validId(meta.id)) throw new Error('outbox: invalid item id');
      const fs = await fsModule();
      await ensureDir(fs);
      const bytes = await readBlobBytes(ciphertextBlob);
      // Bytes first, meta second: a crash between the two leaves a .bin with
      // no .json, which list() never surfaces — never a meta pointing at
      // ciphertext that does not exist.
      await fs.writeFile(binPath(meta.id), bytes, { baseDir: fs.BaseDirectory.AppLocalData });
      await fs.writeTextFile(metaPath(meta.id), JSON.stringify(meta), {
        baseDir: fs.BaseDirectory.AppLocalData,
      });
      return meta.id;
    },

    async list() {
      const fs = await fsModule();
      let entries;
      try {
        entries = await fs.readDir(OUTBOX_DIR, { baseDir: fs.BaseDirectory.AppLocalData });
      } catch {
        return []; // directory not created yet — nothing queued
      }
      const metas = [];
      for (const entry of entries || []) {
        const name = entry?.name || '';
        if (!name.endsWith('.json')) continue;
        try {
          const text = await fs.readTextFile(`${OUTBOX_DIR}/${name}`, {
            baseDir: fs.BaseDirectory.AppLocalData,
          });
          const meta = JSON.parse(text);
          if (meta && validId(meta.id)) metas.push(meta);
        } catch {
          // Corrupt/unreadable meta: skip it rather than failing the sweep.
        }
      }
      return metas;
    },

    async readCiphertext(id) {
      if (!validId(id)) return null;
      const fs = await fsModule();
      try {
        const bytes = await fs.readFile(binPath(id), { baseDir: fs.BaseDirectory.AppLocalData });
        return new Blob([bytes], { type: 'application/octet-stream' });
      } catch {
        return null;
      }
    },

    async updateMeta(id, patch) {
      if (!validId(id)) return;
      const fs = await fsModule();
      let meta;
      try {
        const text = await fs.readTextFile(metaPath(id), {
          baseDir: fs.BaseDirectory.AppLocalData,
        });
        meta = JSON.parse(text);
      } catch {
        return; // item removed concurrently — nothing to patch
      }
      await fs.writeTextFile(metaPath(id), JSON.stringify({ ...meta, ...patch }), {
        baseDir: fs.BaseDirectory.AppLocalData,
      });
    },

    async retagScope(fromScopeKey, toScopeKey) {
      const metas = await this.list();
      for (const meta of metas) {
        if (meta.scopeKey === fromScopeKey) {
          await this.updateMeta(meta.id, { scopeKey: toScopeKey });
        }
      }
    },

    async remove(id) {
      if (!validId(id)) return;
      const fs = await fsModule();
      for (const path of [binPath(id), metaPath(id)]) {
        try {
          await fs.remove(path, { baseDir: fs.BaseDirectory.AppLocalData });
        } catch {
          // already gone — removal is idempotent
        }
      }
    },
  };
}

/* ── Web adapter — IndexedDB (real-IDB behavior untested; see header) ────── */

export function createWebOutboxAdapter(idbFactory) {
  const getIdb = idbFactory || (() => (typeof indexedDB === 'undefined' ? null : indexedDB));
  let dbPromise = null;

  function openDb() {
    const idb = getIdb();
    if (!idb) return Promise.reject(new Error('IndexedDB unavailable'));
    if (!dbPromise) {
      dbPromise = new Promise((resolve, reject) => {
        const req = idb.open(OUTBOX_IDB_NAME, OUTBOX_IDB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(OUTBOX_IDB_STORE)) {
            db.createObjectStore(OUTBOX_IDB_STORE, { keyPath: 'meta.id' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          dbPromise = null;
          reject(req.error || new Error('IndexedDB open failed'));
        };
      });
    }
    return dbPromise;
  }

  function op(mode, run) {
    return openDb().then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction(OUTBOX_IDB_STORE, mode);
          const store = tx.objectStore(OUTBOX_IDB_STORE);
          const req = run(store);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
        })
    );
  }

  return {
    async enqueue(meta, ciphertextBlob) {
      if (!meta || !validId(meta.id)) throw new Error('outbox: invalid item id');
      await op('readwrite', (store) => store.put({ meta, blob: ciphertextBlob }));
      return meta.id;
    },

    async list() {
      try {
        const records = await op('readonly', (store) => store.getAll());
        return (records || []).map((r) => r.meta).filter((m) => m && validId(m.id));
      } catch {
        return []; // no IDB / unopenable db — behave like an empty outbox
      }
    },

    async readCiphertext(id) {
      if (!validId(id)) return null;
      try {
        const record = await op('readonly', (store) => store.get(id));
        return record?.blob ?? null;
      } catch {
        return null;
      }
    },

    async updateMeta(id, patch) {
      if (!validId(id)) return;
      const record = await op('readonly', (store) => store.get(id));
      if (!record) return;
      await op('readwrite', (store) =>
        store.put({ meta: { ...record.meta, ...patch }, blob: record.blob })
      );
    },

    async retagScope(fromScopeKey, toScopeKey) {
      let records;
      try {
        records = await op('readonly', (store) => store.getAll());
      } catch {
        return;
      }
      for (const record of records || []) {
        if (record?.meta?.scopeKey === fromScopeKey && validId(record.meta.id)) {
          await op('readwrite', (store) =>
            store.put({ meta: { ...record.meta, scopeKey: toScopeKey }, blob: record.blob })
          );
        }
      }
    },

    async remove(id) {
      if (!validId(id)) return;
      try {
        await op('readwrite', (store) => store.delete(id));
      } catch {
        // no IDB — nothing stored, nothing to remove
      }
    },
  };
}

/* ── In-memory fake — the SAME interface, for unit tests ─────────────────── */

/**
 * Backs service/unit tests (jsdom has neither IndexedDB nor a real Tauri fs).
 * Deliberately exported from the module itself so the fake and the real
 * adapters can never drift apart silently: the outbox contract test runs the
 * identical assertions against this and against the Tauri adapter.
 */
export function createMemoryOutboxAdapter() {
  const items = new Map(); // id -> { meta, blob }
  return {
    async enqueue(meta, ciphertextBlob) {
      if (!meta || !validId(meta.id)) throw new Error('outbox: invalid item id');
      items.set(meta.id, { meta: { ...meta }, blob: ciphertextBlob });
      return meta.id;
    },
    async list() {
      return [...items.values()].map((r) => ({ ...r.meta }));
    },
    async readCiphertext(id) {
      if (!validId(id)) return null;
      return items.get(id)?.blob ?? null;
    },
    async updateMeta(id, patch) {
      if (!validId(id)) return;
      const record = items.get(id);
      if (record) record.meta = { ...record.meta, ...patch };
    },
    async retagScope(fromScopeKey, toScopeKey) {
      items.forEach((record) => {
        if (record.meta.scopeKey === fromScopeKey) {
          record.meta = { ...record.meta, scopeKey: toScopeKey };
        }
      });
    },
    async remove(id) {
      items.delete(id);
    },
    /** Test-only peek — not part of the adapter interface. */
    __size() {
      return items.size;
    },
  };
}

/* ── The one interface the service and component consume ─────────────────── */

let overrideAdapter = null;
let activeAdapter = null;

function adapter() {
  if (overrideAdapter) return overrideAdapter;
  if (!activeAdapter) {
    activeAdapter = isTauri() ? createTauriOutboxAdapter() : createWebOutboxAdapter();
  }
  return activeAdapter;
}

/** Test seam: swap the storage adapter (pass null to restore auto-detection). */
export function __setOutboxAdapterForTests(nextAdapter) {
  overrideAdapter = nextAdapter || null;
  activeAdapter = null;
}

/** Persist one staged upload (meta + ciphertext). Resolves the item id. */
export function enqueue(meta, ciphertextBlob) {
  return adapter().enqueue(meta, ciphertextBlob);
}

/** All persisted item metas (no blobs). Missing storage -> []. */
export function list() {
  return adapter().list();
}

/** The queued ciphertext for an item, or null when the outbox has nothing. */
export function readCiphertext(id) {
  return adapter().readCiphertext(id);
}

/** Shallow-merge a patch into an item's meta. No-op for unknown ids. */
export function updateMeta(id, patch) {
  return adapter().updateMeta(id, patch);
}

/** Rewrites every item's scopeKey from -> to (the sign-in drafts fold). */
export function retagScope(fromScopeKey, toScopeKey) {
  return adapter().retagScope(fromScopeKey, toScopeKey);
}

/** Drop an item (meta + ciphertext). Idempotent. */
export function remove(id) {
  return adapter().remove(id);
}
